import { useState } from 'react'
import { useEffect } from 'react'

import { BULK_SLEEP_TIME, MAX_BULK_REQUEST_SIZE } from 'meta/pages/v4/bulk'
import { useAppDispatch } from 'store'
import { TicketActions } from 'store/pages/ticket'
import { TypeTicketActions } from 'store/pages/typeticket'
import { TypeTicketCollectionActions } from 'store/pages/typeticketcollection'
import { WalletActions } from 'store/pages/wallet'
import { chunkArrayInGroups } from 'utils/array'

import { useTypeTicketsIdsForGivenEvent } from 'hooks/useTicket'
import { useTypeTicketsCollectionIdsForGivenEvent } from 'hooks/useTypeTicket'
import { useWalletEventTicketCodes, useWalletEventTicketCodesMetadata } from 'hooks/useWallet'

interface Props {
  eventId: number
}

const useBulkUserWalletTicketData = (props: Props) => {
  const { eventId } = props

  const dispatch = useAppDispatch()

  const eventTicketCodesData = useWalletEventTicketCodes({ eventIdOrCodeUrl: String(eventId) })
  const eventTicketCodesMetadata = useWalletEventTicketCodesMetadata({ eventIdOrCodeUrl: String(eventId) })

  const typeTicketIds = useTypeTicketsIdsForGivenEvent({ eventId: eventId })
  const typeTicketCollectionIds = useTypeTicketsCollectionIdsForGivenEvent({ eventId: eventId })

  const [fetchedTypeTicketsNum, setFetchedTypeTicketNum] = useState<number>(0)
  const [fetchedTypeTicketCollectionsNum, setFetchedTypeTicketCollectionsNum] = useState<number>(0)

  useEffect(() => {
    let cancelled = false
    const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms))

    if (!eventTicketCodesData) {
      dispatch(WalletActions.getEventTicketCodes({ eventIdOrCodeUrl: String(eventId), page: 1 }))
    } else {
      if (eventTicketCodesMetadata && !eventTicketCodesMetadata.hasMorePages) {
        const chunks = chunkArrayInGroups(eventTicketCodesData, MAX_BULK_REQUEST_SIZE)

        ;(async () => {
          for (const chunk of chunks) {
            if (cancelled) break
            await dispatch(TicketActions.getBulkData({ ids: chunk.map((x) => x.cod), eventId }))
            await sleep(BULK_SLEEP_TIME)
          }
        })()
      }
    }

    return () => {
      cancelled = true
    }
  }, [eventTicketCodesData, eventTicketCodesMetadata])

  useEffect(() => {
    if (eventTicketCodesMetadata && eventTicketCodesMetadata.hasMorePages) {
      dispatch(
        WalletActions.getEventTicketCodes({
          eventIdOrCodeUrl: String(eventId),
          page: eventTicketCodesMetadata.currentPage + 1,
        })
      )
    }
  }, [eventTicketCodesMetadata])

  useEffect(() => {
    const uniqueTypeTicketIds = [...new Set(typeTicketIds)]

    if (uniqueTypeTicketIds && uniqueTypeTicketIds.length > fetchedTypeTicketsNum) {
      chunkArrayInGroups(uniqueTypeTicketIds, MAX_BULK_REQUEST_SIZE).map((chunk) => {
        dispatch(TypeTicketActions.getBulkData({ ids: chunk, eventId }))
        setFetchedTypeTicketNum(uniqueTypeTicketIds.length)
      })
    }
  }, [typeTicketIds])

  useEffect(() => {
    const uniqueTypeTicketCollectionIds = [...new Set(typeTicketCollectionIds)]

    if (uniqueTypeTicketCollectionIds && uniqueTypeTicketCollectionIds.length > fetchedTypeTicketCollectionsNum) {
      chunkArrayInGroups(uniqueTypeTicketCollectionIds, MAX_BULK_REQUEST_SIZE).map((chunk) => {
        dispatch(TypeTicketCollectionActions.getBulkData({ ids: chunk, eventId }))
        setFetchedTypeTicketCollectionsNum(uniqueTypeTicketCollectionIds.length)
      })
    }
  }, [typeTicketCollectionIds])
}

export default useBulkUserWalletTicketData
